import React, { useState, useEffect } from 'react';
import ProfileCompletion from '../components/ProfileCompletion';
import AdminHomeButton from '../components/AdminHomeButton';
import BlueTickBadge from '../components/BlueTickBadge';
import { getPiConversion } from '../components/piCurrency';
import { t, getLang, LANGUAGES } from '../i18n';

const THEME_GRADIENTS = {
  midnight: 'linear-gradient(135deg, #1a3a6e, #0d1f3c, #070b14)',
  forest: 'linear-gradient(135deg, #16a34a, #0d1f18, #071410)',
  rose: 'linear-gradient(135deg, #be123c, #211018, #140b0f)',
};
function getLandingGradient() {
  const theme = localStorage.getItem('xpert_theme') || 'midnight';
  return THEME_GRADIENTS[theme] || THEME_GRADIENTS.midnight;
}


function formatDate(dateStr) {
  const d = new Date(dateStr);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${d.getDate()}-${months[d.getMonth()]}-${d.getFullYear()} ${hours}:${minutes} ${ampm}`;
}

function linkify(text) {
  const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (part.match(urlRegex)) {
      const href = part.startsWith('http') ? part : `https://${part}`;
      return <a key={i} href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#60a5fa', wordBreak: 'break-all' }}>{part}</a>;
    }
    return part;
  });
}

function Nav3DButton({ emoji, label, sub, onClick, color }) {
  const [pressed, setPressed] = useState(false);
  const [hovered, setHovered] = useState(false);
  const touchStartY = React.useRef(null);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => { setHovered(false); setPressed(false); }}
      onMouseDown={() => setPressed(true)}
      onMouseUp={() => setPressed(false)}
      onTouchStart={(e) => { setPressed(true); touchStartY.current = e.touches[0].clientY; }}
      onTouchEnd={(e) => {
        setPressed(false);
        const diff = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
        if (diff < 10) { onClick && onClick(); }
      }}
      style={{
        background: pressed
          ? `linear-gradient(145deg, ${color}cc, ${color})`
          : hovered
          ? `linear-gradient(145deg, ${color}, ${color}dd)`
          : `linear-gradient(145deg, ${color}ee, ${color}99)`,
        borderRadius: '17px',
        padding: '14px 5px',
        textAlign: 'center',
        cursor: 'pointer',
        boxShadow: pressed
          ? `0 2px 0 ${color}44, inset 0 2px 6px rgba(0,0,0,0.3)`
          : hovered
          ? `0 12px 30px ${color}66, 0 6px 0 ${color}55`
          : `0 8px 0 ${color}44, 0 12px 20px rgba(0,0,0,0.3)`,
        transform: pressed ? 'translateY(6px)' : hovered ? 'translateY(-4px)' : 'translateY(0)',
        transition: 'all 0.15s ease',
        border: `1px solid ${color}88`,
        width: '100%',
      }}
    >
      <div style={{ fontSize: '28px', marginBottom: '4px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.4))' }}>{emoji}</div>
      <div style={{ color: 'white', fontWeight: 800, fontSize: '13px', lineHeight: 1.2, textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>{label}</div>
      <div style={{ color: 'rgba(255,255,255,0.78)', fontSize: '9.5px', marginTop: '3px', lineHeight: 1.25 }}>{sub}</div>
    </div>
  );
}


const SHARE_BTNS = [
  { bg: '#25D366', label: 'WhatsApp', path: 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 16.582c-.252.717-1.493 1.408-2.045 1.494-.523.08-1.175.114-1.896-.119-.437-.138-.997-.322-1.713-.631-3.007-1.298-4.973-4.331-5.123-4.532-.149-.198-1.213-1.613-1.213-3.078 0-1.464.769-2.185 1.042-2.484.272-.298.595-.373.794-.373h.573c.184 0 .434-.07.681.519.254.602.863 2.095.937 2.247.075.152.125.328-.025.527-.149.198-.224.322-.372.496-.149.174-.312.389-.178.636.174.298.772 1.273 1.657 2.062 1.138 1.014 2.099 1.328 2.397 1.478.298.149.473.124.647-.075.174-.198.744-.867.942-1.165.198-.298.397-.249.67-.149.272.099 1.735.817 2.033.966.298.149.497.224.572.348.074.124.074.719-.178 1.436z', getHref: (t,u) => `https://wa.me/?text=${t}%20${u}` },
  { bg: '#000', label: 'X/Twitter', path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.748l7.73-8.835L1.254 2.25H8.08l4.259 5.63zm-1.161 17.52h1.833L7.084 4.126H5.117z', getHref: (t,u) => `https://twitter.com/intent/tweet?text=${t}&url=${u}` },
  { bg: '#0088cc', label: 'Telegram', path: 'M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L6.196 13.9l-2.948-.924c-.64-.203-.654-.64.135-.954l11.566-4.458c.537-.194 1.006.131.945.657z', getHref: (t,u) => `https://t.me/share/url?url=${u}&text=${t}` },
  { bg: '#1877F2', label: 'Facebook', path: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z', getHref: (t,u) => `https://www.facebook.com/sharer/sharer.php?u=${u}` },
];

function ShareIcons({ title, url }) {
  const t = encodeURIComponent(`Check out: ${title}`);
  const u = encodeURIComponent(url);
  return (
    <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
      {SHARE_BTNS.map(b => (
        <a key={b.label} href={b.getHref(t,u)} target="_blank" rel="noopener noreferrer" title={b.label}
          style={{ width: 30, height: 30, borderRadius: '50%', background: b.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 4px rgba(0,0,0,0.2)', flexShrink: 0 }}>
          <svg viewBox="0 0 24 24" width="16" height="16" fill="white"><path d={b.path} /></svg>
        </a>
      ))}
    </div>
  );
}

// Pi Browser ek WebView hai — target="_blank" bahar nahi kholta, aur
// t.me ka web page us ke andar toot jata hai.
// tg:// seedha Telegram app kholta hai. App na ho to 1 second baad web par.
function openTelegram(e) {
  if (e) e.preventDefault();
  const app = 'tg://resolve?domain=XpertCreation';
  const web = 'https://t.me/XpertCreation';
  let gone = false;
  const bye = () => { gone = true; };
  document.addEventListener('visibilitychange', bye, { once: true });
  window.addEventListener('pagehide', bye, { once: true });
  window.location.href = app;
  setTimeout(() => {
    document.removeEventListener('visibilitychange', bye);
    if (!gone && !document.hidden) window.open(web, '_blank', 'noopener');
  }, 1000);
}

function Home({ user }) {
  const [posts, setPosts] = useState([]);
  const [boostedIds, setBoostedIds] = useState([]);
  const [country, setCountry] = useState({ flag: '🌍', name: '' });
  const [weather, setWeather] = useState(null);
  const [piRate, setPiRate] = useState(null);
  const [cryptoRates, setCryptoRates] = useState(null);
  const [localCurrency, setLocalCurrency] = useState(null);
  const [userDob, setUserDob] = useState(null);
  const [isBirthday, setIsBirthday] = useState(false);
  const [showBdayCard, setShowBdayCard] = useState(false);

  React.useEffect(() => {
    getPiConversion(1).then((conv) => { if (conv && conv.local !== null) setLocalCurrency(conv); }).catch(() => {});
  }, []);

  React.useEffect(() => {
    // Mausam user ke APNE maqam ka - geojs pehle se lat/long deta hai,
    // pehle wo istemal hi nahi hota tha aur Lahore hardcoded tha.
    const loadWeather = (lat, lon, place) => {
      fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true`)
        .then(r => r.json())
        .then(w => {
          const cw = w && w.current_weather;
          if (!cw) return;
          const code = cw.weathercode;
          const temp = Math.round(cw.temperature);
          const emoji = code === 0 ? '☀️' : code <= 2 ? '⛅' : code <= 3 ? '☁️' : code <= 48 ? '🌫️' : code <= 67 ? '🌧️' : '⛈️';
          setWeather({ temp, emoji, place: place || '' });
        })
        .catch(() => {});
    };
    fetch('https://get.geojs.io/v1/ip/geo.json')
      .then(r => r.json())
      .then(d => {
        const code = d.country_code || '';
        const flagEmoji = code
          ? code.toUpperCase().replace(/./g, c => String.fromCodePoint(c.charCodeAt(0) + 127397))
          : '🌍';
        setCountry({ flag: flagEmoji, name: d.country || '' });
        if (d.latitude && d.longitude) {
          loadWeather(d.latitude, d.longitude, d.city || d.country || '');
        } else {
          loadWeather(31.5204, 74.3587, 'Lahore');
        }
      })
      .catch(() => {
        setCountry({ flag: '🌍', name: '' });
        loadWeather(31.5204, 74.3587, 'Lahore');   // geojs block ho to bhi mausam aaye
      });

    // (weather ab upar loadWeather() se aata hai)

    // Crypto rates
    fetch('https://api.coingecko.com/api/v3/simple/price?ids=bitcoin,ethereum&vs_currencies=usd')
      .then(r => r.json()).then(d => setCryptoRates({ btc: d.bitcoin?.usd, eth: d.ethereum?.usd }))
      .catch((e) => {
});

    fetch('https://api.coingecko.com/api/v3/simple/price?ids=pi-network&vs_currencies=usd')
      .then(r => r.json()).then(d => { if (d['pi-network']?.usd) setPiRate(d['pi-network'].usd); })
      .catch(() => {});
  }, []);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('date');
  const [voting, setVoting] = useState({});
  const [view, setView] = useState('landing');
  const [lang, setLangState] = useState(getLang());
  useEffect(() => {
    const checkLang = () => {
      const current = getLang();
      setLangState(current);
    };
    window.addEventListener('storage', checkLang);
    const interval = setInterval(checkLang, 300);
    return () => { clearInterval(interval); window.removeEventListener('storage', checkLang); };
  }, []);
  const tr = (key) => {
    const { T } = require('../i18n');
    return T[lang]?.[key] || T['en'][key] || key;
  };
  const [openComment, setOpenComment] = useState({});
  const [commentText, setCommentText] = useState({});
  const [submittingComment, setSubmittingComment] = useState({});
  const [openTip, setOpenTip] = useState({});
  const [tipAmount, setTipAmount] = useState({});

  const loadPosts = () => {
    Promise.all([
      fetch('/api/posts/').then(r => r.json()),
      fetch('/api/posts/boosted/').then(r => r.json()),
    ]).then(([postsData, boostedData]) => {
      setPosts(postsData);
      setBoostedIds(boostedData.boosted_post_ids || []);
      setLoading(false);
    }).catch(err => { console.error(err); setLoading(false); });
  };

  useEffect(() => { loadPosts(); }, []);

  useEffect(() => {
    if (!user) return;
    fetch(`/api/profile/${user.username}/`)
      .then(r => r.json())
      .then(d => {
        if (d.date_of_birth) {
          setUserDob(d.date_of_birth);
          const dob = new Date(d.date_of_birth + 'T00:00:00');
          const today = new Date();
          if (dob.getDate() === today.getDate() && dob.getMonth() === today.getMonth()) {
            setIsBirthday(true);
            setShowBdayCard(true);
          }
        }
      }).catch(() => {});
  }, [user]);

  const handleVote = async (postId) => {
    if (!user) { alert('Please login to vote!'); return; }
    setVoting(prev => ({ ...prev, [postId]: true }));
    try {
      await fetch(`/api/posts/${postId}/vote/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${user.token}` }
      });
      loadPosts();
    } catch (err) { console.error(err); }
    setVoting(prev => ({ ...prev, [postId]: false }));
  };

  const handleComment = async (postId) => {
    if (!user) { alert('Please login to comment!'); return; }
    const text = commentText[postId] || '';
    if (!text.trim()) return;
    setSubmittingComment(prev => ({ ...prev, [postId]: true }));
    try {
      await fetch(`/api/posts/${postId}/comments/`, {
        method: 'POST',
        headers: { 'Authorization': `Token ${user.token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: text })
      });
      setCommentText(prev => ({ ...prev, [postId]: '' }));
      setOpenComment(prev => ({ ...prev, [postId]: false }));
      loadPosts();
    } catch (err) { console.error(err); }
    setSubmittingComment(prev => ({ ...prev, [postId]: false }));
  };

  const filtered = posts
    .filter(p =>
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.author.username.toLowerCase().includes(search.toLowerCase()) ||
      p.body.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      const aBoost = boostedIds.includes(a.id) ? 1 : 0;
      const bBoost = boostedIds.includes(b.id) ? 1 : 0;
      if (bBoost !== aBoost) return bBoost - aBoost;
      if (sortBy === 'date') return new Date(b.created_at) - new Date(a.created_at);
      if (sortBy === 'votes') return b.total_votes - a.total_votes;
      if (sortBy === 'earnings') return parseFloat(b.pi_reward_pool) - parseFloat(a.pi_reward_pool);
      if (sortBy === 'user') return a.author.username.localeCompare(b.author.username);
      return 0;
    });

  const getZodiac = (dobStr) => {
    const d = dobStr ? new Date(dobStr + 'T00:00:00') : new Date();
    const m = d.getMonth() + 1, day = d.getDate();
    if ((m===12&&day>=22)||(m===1&&day<=19)) return {sign:'Capricorn',emoji:'🐐',desc:'Discipline pays off today!'};
    if ((m===1&&day>=20)||(m===2&&day<=18)) return {sign:'Aquarius',emoji:'🏺',desc:'Innovation is your theme today!'};
    if ((m===2&&day>=19)||(m===3&&day<=20)) return {sign:'Pisces',emoji:'🐟',desc:'Trust your intuition today!'};
    if ((m===3&&day>=21)||(m===4&&day<=19)) return {sign:'Aries',emoji:'🐏',desc:'Take bold steps today!'};
    if ((m===4&&day>=20)||(m===5&&day<=20)) return {sign:'Taurus',emoji:'🐂',desc:'Focus on stability today!'};
    if ((m===5&&day>=21)||(m===6&&day<=20)) return {sign:'Gemini',emoji:'👯',desc:'Communication flows well!'};
    if ((m===6&&day>=21)||(m===7&&day<=22)) return {sign:'Cancer',emoji:'🦀',desc:'Trust your emotions today!'};
    if ((m===7&&day>=23)||(m===8&&day<=22)) return {sign:'Leo',emoji:'🦁',desc:'You are shining today!'};
    if ((m===8&&day>=23)||(m===9&&day<=22)) return {sign:'Virgo',emoji:'🌾',desc:'Details matter today!'};
    if ((m===9&&day>=23)||(m===10&&day<=22)) return {sign:'Libra',emoji:'⚖️',desc:'Balance and harmony today!'};
    if ((m===10&&day>=23)||(m===11&&day<=21)) return {sign:'Scorpio',emoji:'🦂',desc:'Trust your instincts!'};
    if ((m===11&&day>=22)||(m===12&&day<=21)) return {sign:'Sagittarius',emoji:'🏹',desc:'Adventure calls today!'};
    return {sign:'Capricorn',emoji:'🐐',desc:'Hard work pays off!'};
  };

  if (view === 'landing') {
    return (
      <div style={styles.landing}>
        {/* Animated background */}
        <div style={styles.bgOrb1} />
        <div style={styles.bgOrb2} />
        <div style={styles.bgOrb3} />

        <div style={styles.landingInner}>
          {/* Username Banner */}

          {/* Logo */}
          <div style={styles.logoArea}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '4px', width: '100%' }}>
              <span style={{ fontSize: '28px' }}>✍️</span>
              <link href="https://fonts.googleapis.com/css2?family=Pacifico&display=swap" rel="stylesheet" />
              <h1 style={styles.logoTitle}>PenFlow</h1>
            </div>
            <p style={styles.logoTagline}>Write · Earn Pi · Connect</p>
            {/* Crypto Rates */}
            <div style={{ display: 'flex', gap: '4px', marginTop: '6px', justifyContent: 'center', flexWrap: 'nowrap', overflowX: 'auto', padding: '0 6px' }}>
              {cryptoRates?.btc && (
                <a href="https://www.coingecko.com/en/coins/bitcoin" target="_blank" rel="noopener noreferrer"
                  style={{ display:'flex', alignItems:'center', gap:'3px', color:'#f7931a', fontSize:'11px', textDecoration:'none', backgroundColor:'rgba(247,147,26,0.15)', padding:'3px 8px', borderRadius:'10px', border:'1px solid rgba(247,147,26,0.4)', fontWeight:'bold' }}>
                  <img src="https://assets.coingecko.com/coins/images/1/small/bitcoin.png" alt="BTC" style={{width:'12px',height:'12px',borderRadius:'50%'}} />
                  BTC ${cryptoRates.btc?.toLocaleString()}
                </a>
              )}
              {cryptoRates?.eth && (
                <a href="https://www.coingecko.com/en/coins/ethereum" target="_blank" rel="noopener noreferrer"
                  style={{ display:'flex', alignItems:'center', gap:'3px', color:'#60a5fa', fontSize:'11px', textDecoration:'none', backgroundColor:'rgba(96,165,250,0.15)', padding:'3px 8px', borderRadius:'10px', border:'1px solid rgba(96,165,250,0.4)', fontWeight:'bold' }}>
                  <img src="https://assets.coingecko.com/coins/images/279/small/ethereum.png" alt="ETH" style={{width:'12px',height:'12px',borderRadius:'50%'}} />
                  ETH ${cryptoRates.eth?.toLocaleString()}
                </a>
              )}
              {piRate && (
                <a href="https://www.coingecko.com/en/coins/pi-network" target="_blank" rel="noopener noreferrer"
                  style={{ display:'flex', alignItems:'center', gap:'3px', color:'#34d399', fontSize:'11px', textDecoration:'none', backgroundColor:'rgba(52,211,153,0.15)', padding:'3px 8px', borderRadius:'10px', border:'1px solid rgba(52,211,153,0.4)', fontWeight:'bold' }}>
                  <span style={{fontSize:'11px',fontWeight:'900',fontFamily:'serif'}}>π</span>
                  PI ${piRate}
                </a>
              )}
              {localCurrency && (
                <span style={{ display:'flex', alignItems:'center', gap:'3px', color:'#94a3b8', fontSize:'11px', backgroundColor:'rgba(148,163,184,0.15)', padding:'3px 8px', borderRadius:'10px', border:'1px solid rgba(148,163,184,0.4)', fontWeight:'bold' }}>
                  1π ≈ {localCurrency.localSymbol}{localCurrency.local}
                </span>
              )}
            </div>
                        <div style={{ overflow: 'hidden', whiteSpace: 'nowrap', marginTop: '4px', marginBottom: '2px' }}>
              <div className="marqueeTicker" style={{ display: 'inline-block', paddingLeft: '100%', animation: 'marquee-scroll 40s linear infinite', color: 'rgba(255,255,255,0.6)', fontSize: '11px' }}>
                {'\u{1F30D} Write, Earn Pi, and Connect with a global community!'}
              </div>
            </div>
            <style>{`@keyframes marquee-scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-100%); } }`}</style>
            {/* Welcome + date (app.xpertcreation.com jaisa) */}
            <div style={{ textAlign: 'center', marginTop: '8px', marginBottom: '2px' }}>
              <div style={{ fontSize: '15px', fontWeight: 900, color: '#34d399' }}>
                {user ? `✨ ${t('hm_welcome_back')}, @${user.username}!` : `✨ ${t('hm_welcome_to')}`}
              </div>
              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                📅 {new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })} · 🕐 {new Date().toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
            <ProfileCompletion user={user} compact />
            <AdminHomeButton user={user} />
            <div style={styles.statsRow}>
              <a href="#posts" onClick={() => setView('posts')} style={styles.statPillLink}>📝 {posts.length} Posts</a>
              <a href="/leaderboard" style={styles.statPillLink}>👥 {[...new Set(posts.map(p => p.author.username))].length} Writers</a>
              <a href="/leaderboard" style={styles.statPillLink}>👍 {posts.reduce((s, p) => s + p.total_votes, 0)} Votes</a>
            </div>
{/* Weather + Zodiac */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px', padding: '0 6px', boxSizing: 'border-box' }}>
              {weather && (
                <div
                  onClick={() => { window.location.href = '/weather'; }}
                  style={{ flex: 1, cursor: 'pointer', backgroundColor: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.3)', borderRadius: '10px', padding: '6px 8px', textAlign: 'center' }}
                >
                  <div style={{ fontSize: '20px', marginBottom: '2px', color: '#fff' }}>{weather.emoji}</div>
                  <div style={{ fontSize: '11px', color: '#60a5fa', fontWeight: 900 }}>{weather.temp}°C</div>
                  <div style={{ fontSize: '8px', color: '#94a3b8', marginTop: '1px' }}>
                    {weather.place ? weather.place + ' · ' : ''}{t('view_forecast')} →
                  </div>
                </div>
              )}
              {user && (
                <div onClick={() => { window.location.href = '/zodiac'; }} style={{ flex: 1, display: 'block', cursor: 'pointer', color: '#e2e8f0', backgroundColor: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: '10px', padding: '6px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '20px', marginBottom: '2px', color: '#fff' }}>{userDob ? getZodiac(userDob).emoji : '⭐'}</div>
                  <div style={{ fontSize: '11px', color: '#34d399', fontWeight: 900 }}>{userDob ? getZodiac(userDob).sign : t('hm_set_birthday')}</div>
                  <div style={{ fontSize: '8px', color: '#94a3b8', marginTop: '1px' }}>{userDob ? t('view_horoscope') : t('hm_tap_horoscope')} →</div>
                </div>
              )}
            </div>
            <a href="/leaderboard" style={{ display:'flex', alignItems:'center', justifyContent: 'center', gap:'3px', color:'#34d399', textDecoration:'none', backgroundColor:'rgba(52,211,153,0.12)', padding:'5px 10px', borderRadius:'12px', border:'1px solid rgba(52,211,153,0.3)', fontSize:'11px', fontWeight:'bold', marginTop: '8px' }}>
              🏆 {t('top_100')}
            </a>
          </div>

          {/* 3D Navigation Buttons */}
          <div style={styles.navSections}>
            <div style={styles.navSection}>
              <div style={styles.navSectionHead}>
                <span style={styles.navSectionIcon}>💰</span>
                <span style={styles.navSectionTitle}>{t('nav_cat_earn')}</span>
                <span style={styles.navSectionLine} />
              </div>
              <div style={styles.btnGrid}>
                  <Nav3DButton
                                emoji="📰"
                                label={tr("posts")}
                                sub={tr("read_earn")}
                                color="#1d4ed8"
                                onClick={() => setView('posts')}
                              />
                  <Nav3DButton
                                emoji="✍️"
                                label={tr("write")}
                                sub={tr("share_story")}
                                color="#0ea5e9"
                                onClick={() => user ? window.location.href = '/write' : alert('Please login first!')}
                              />
                  <Nav3DButton
                                emoji="🎬"
                                label={tr("xperttube")}
                                sub={tr("watch_upload")}
                                color="#2563eb"
                                onClick={() => window.location.href = '/videos'}
                              />
                  <Nav3DButton
                                  emoji="💼"
                                  label={tr("freelance_nav")}
                                  sub={tr("hire_or_get_hired")}
                                  color="#2563eb"
                                  onClick={() => window.location.href = '/freelance'}
                                />
                  <Nav3DButton
                                  emoji="🗣️"
                                  label={tr("forum_nav")}
                                  sub={tr("ask_discuss")}
                                  color="#2563eb"
                                  onClick={() => window.location.href = '/forum'}
                                />
                  <Nav3DButton
                                emoji="🏢"
                                label="XpertCareers"
                                sub="Find a job"
                                color="#059669"
                                onClick={() => window.location.href = '/careers'}
                              />
                  <Nav3DButton
                                  emoji="🎮"
                                  label={tr("games_nav")}
                                  sub={tr("play_compete")}
                                  color="#0d9488"
                                  onClick={() => window.location.href = '/games'}
                                />
                  <Nav3DButton
                                emoji="💼"
                                label="Tasks"
                                sub="Earn Pi"
                                color="#0F6E56"
                                onClick={() => window.location.href = '/tasks'}
                              />
                  <Nav3DButton
                                emoji="🎁"
                                label="Airdrops"
                                sub="Win Pi"
                                color="#0d9488"
                                onClick={() => window.location.href = '/airdrops'}
                              />
              </div>
            </div>
            <div style={styles.navSection}>
              <div style={styles.navSectionHead}>
                <span style={styles.navSectionIcon}>🛍</span>
                <span style={styles.navSectionTitle}>{t('nav_cat_market')}</span>
                <span style={styles.navSectionLine} />
              </div>
              <div style={styles.btnGrid}>
                  <Nav3DButton
                                emoji="🗺️"
                                label={tr("xpert_dpin")}
                                  sub={tr("storage_compute")}
                                  color="#0d9488"
                                  onClick={() => window.location.href = '/dpin'}
                                />
                  <Nav3DButton
                                  emoji="🛍️"
                                  label={tr("shopping_nav")}
                                  sub={tr("buy_or_sell")}
                                  color="#0F6E56"
                                  onClick={() => window.location.href = '/shopping'}
                                />
                  <Nav3DButton
                                  emoji="🏠"
                                  label={tr("realty_nav")}
                                  sub={tr("buy_sell_rent")}
                                  color="#0d9488"
                                  onClick={() => window.location.href = '/realty'}
                                />
                  <Nav3DButton
                                  emoji="🏨"
                                  label={tr("travel_nav")}
                                  sub={tr("book_a_stay")}
                                  color="#0369a1"
                                  onClick={() => window.location.href = '/travel'}
                                />
                  <Nav3DButton
                                  emoji="🖼️"
                                  label={tr("nft_nav")}
                                  sub={tr("mint_trade")}
                                  color="#1d4ed8"
                                  onClick={() => window.location.href = '/nft'}
                                />
              </div>
            </div>
            <div style={styles.navSection}>
              <div style={styles.navSectionHead}>
                <span style={styles.navSectionIcon}>🩺</span>
                <span style={styles.navSectionTitle}>{t('nav_cat_health')}</span>
                <span style={styles.navSectionLine} />
              </div>
              <div style={styles.btnGrid}>
                  <Nav3DButton
                                  emoji="🐾"
                                  label={tr("petcare_nav")}
                                  sub={tr("tags_health")}
                                  color="#a16207"
                                  onClick={() => window.location.href = '/petcare'}
                                />
                  <Nav3DButton
                                emoji="🩺"
                                label="Health"
                                sub="Book a doctor"
                                color="#0D9488"
                                onClick={() => window.location.href = '/health'}
                              />
                  <Nav3DButton
                                emoji="🩸"
                                label="Blood Bank"
                                sub="Donate & find blood"
                                color="#C0392B"
                                onClick={() => window.location.href = '/blood-bank'}
                              />
              </div>
            </div>
            <div style={styles.navSection}>
              <div style={styles.navSectionHead}>
                <span style={styles.navSectionIcon}>🎓</span>
                <span style={styles.navSectionTitle}>{t('nav_cat_learn')}</span>
                <span style={styles.navSectionLine} />
              </div>
              <div style={styles.btnGrid}>
                  <Nav3DButton
                                  emoji="🎓"
                                  label={tr("academy_nav")}
                                  sub={tr("learn_or_teach")}
                                  color="#0f766e"
                                  onClick={() => window.location.href = '/academy'}
                                />
                  <Nav3DButton
                                emoji="📄"
                                label="CV Builder"
                                sub="Build your resume"
                                color="#4F46E5"
                                onClick={() => window.location.href = '/cv-builder'}
                              />
              </div>
            </div>
            <div style={styles.navSection}>
              <div style={styles.navSectionHead}>
                <span style={styles.navSectionIcon}>👥</span>
                <span style={styles.navSectionTitle}>{t('nav_cat_connect')}</span>
                <span style={styles.navSectionLine} />
              </div>
              <div style={styles.btnGrid}>
                  <Nav3DButton
                                  emoji="📅"
                                  label={tr("meetup_nav")}
                                  sub={tr("find_an_event")}
                                  color="#C0392B"
                                  onClick={() => window.location.href = '/meetup'}
                                />
                  <Nav3DButton
                                  emoji="🌳"
                                  label={tr("family_tree_nav")}
                                  sub={tr("your_history")}
                                  color="#15803d"
                                  onClick={() => window.location.href = '/family-tree'}
                                />
                  <Nav3DButton
                                  emoji="💕"
                                  label={tr("matrimony_nav")}
                                  sub={tr("find_a_match")}
                                  color="#be185d"
                                  onClick={() => window.location.href = '/matrimony'}
                                />
                  <Nav3DButton
                                emoji="🤝"
                                label={tr("network")}
                                sub={tr("connect_grow")}
                                color="#0891b2"
                                onClick={() => window.location.href = '/connections'}
                              />
              </div>
            </div>
            <div style={styles.navSection}>
              <div style={styles.navSectionHead}>
                <span style={styles.navSectionIcon}>🛠</span>
                <span style={styles.navSectionTitle}>{t('nav_cat_tools')}</span>
                <span style={styles.navSectionLine} />
              </div>
              <div style={styles.btnGrid}>
                  <Nav3DButton
                                emoji="🔧"
                                label="Pi Debugger"
                                sub="Fix Pi payment code"
                                color="#185FA5"
                                onClick={() => window.location.href = '/pi-debugger'}
                              />
                  <Nav3DButton
                                emoji="⏰"
                                label="Reminders"
                                sub="Track expiry dates"
                                color="#C0392B"
                                onClick={() => window.location.href = '/reminders'}
                              />
                  <Nav3DButton
                                emoji="🎫"
                                label={tr("support")}
                                sub={tr("get_help")}
                                color="#e11d48"
                                onClick={() => window.location.href = '/support'}
                              />
                  <Nav3DButton
                                emoji="🛠️"
                                label={tr("tools")}
                                sub={tr("free_utils")}
                                color="#0d9488"
                                onClick={() => window.location.href = '/tools'}
                              />
              </div>
            </div>
          </div>

          {/* Login only */}
          {!user && (
            <div style={styles.loginBox}>
              <div style={styles.loginBoxInner}>
                <p style={styles.loginText}>🔑 Open in <strong>Pi Browser</strong> to Login & Earn Pi</p>
              </div>
            </div>
          )}

          {/* Footer */}
          <div style={styles.landingFooter}>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', marginBottom: '6px' }}>
              <a href="https://discord.gg/Dtu976rr3u" target="_blank" rel="noopener noreferrer" style={{ color: '#5865F2', textDecoration: 'none', fontSize: '13px', fontWeight: 'bold' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#5865F2" style={{verticalAlign:'middle', marginRight:'4px'}}><path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/></svg>
                Discord
              </a>
              <a href="https://t.me/XpertCreation" onClick={openTelegram} target="_blank" rel="noopener noreferrer" style={{ color: '#0088cc', textDecoration: 'none', fontSize: '13px', fontWeight: 'bold' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="#0088cc" style={{ verticalAlign: 'middle', marginRight: 4 }}>
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.894 8.221l-1.97 9.28c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12L6.196 13.9l-2.948-.924c-.64-.203-.654-.64.135-.954l11.566-4.458c.537-.194 1.006.131.945.657z"/>
                </svg>
                Telegram
              </a>
              <a href="https://twitter.com/khalidxpert" target="_blank" rel="noopener noreferrer" style={{ color: 'white', textDecoration: 'none', fontSize: '13px', fontWeight: 'bold' }}>
                𝕏 @khalidxpert
              </a>
              <a href="/xpertcreation" style={{ color: '#34d399', textDecoration: 'none', fontSize: '13px', fontWeight: 'bold' }}>
                🌐 Ecosystem
              </a>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '11px', margin: 0 }}>© 2026 XpertCreation</p>
          </div>
        </div>
      </div>
    );
  }

  // Posts Feed View
  return (
    <div style={styles.container}>
      <button style={styles.backBtn} onClick={() => setView('landing')}>← Back</button>

      <div style={styles.statsBar}>
        <div style={styles.statItem}><span style={styles.statNum}>{posts.length}</span><span style={styles.statLabel}>{t('posts')}</span></div>
        <div style={styles.statItem}><span style={styles.statNum}>{posts.reduce((s, p) => s + parseFloat(p.pi_reward_pool || 0), 0).toFixed(2)}</span><span style={styles.statLabel}>{t('pi_distributed')}</span></div>
        <div style={styles.statItem}><span style={styles.statNum}>{posts.reduce((s, p) => s + p.total_votes, 0)}</span><span style={styles.statLabel}>{t('votes_label')}</span></div>
        <div style={styles.statItem}><span style={styles.statNum}>{[...new Set(posts.map(p => p.author.username))].length}</span><span style={styles.statLabel}>{t('writers_label')}</span></div>
      </div>

      <div style={styles.searchBar}>
        <input style={styles.searchInput} placeholder="🔍 Search posts, authors..." value={search} onChange={e => setSearch(e.target.value)} />
        <select style={styles.sortSelect} value={sortBy} onChange={e => setSortBy(e.target.value)}>
          <option value="date">📅 Newest</option>
          <option value="votes">👍 Most Voted</option>
          <option value="earnings">💰 Top Earning</option>
          <option value="user">👤 By Author</option>
        </select>
      </div>

      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
        <h2 style={{...styles.feedTitle, margin:0}}>📰 Latest Posts {search && `— "${search}"`}</h2>
        {user && <a href="/write" style={{ backgroundColor:'#3ea6ff', color:'white', padding:'8px 16px', borderRadius:'20px', textDecoration:'none', fontWeight:'bold', fontSize:'13px' }}>✍️ Write Post</a>}
      </div>

      {loading ? (
        <div style={styles.loading}>{t('loading_posts')}</div>
      ) : (
        <div style={styles.feed}>
          {filtered.length === 0 ? (
            <div style={styles.empty}><h2>🌟 {search ? 'No results!' : 'No posts yet!'}</h2></div>
          ) : (
            filtered.map(post => {
              const hasVoted = user && post.votes.some(v => v.voter.username === user.username);
              return (
                <div key={post.id} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <a href={`/profile/${post.author.username}`} style={styles.author}>
                      {post.author.avatar ? <img src={post.author.avatar} alt={post.author.username} style={styles.avatarSmall} /> : '👤 '}
                      {post.author.username}{post.author.has_blue_tick && <BlueTickBadge size={13} />}
                    </a>
                    <span style={styles.date}>{formatDate(post.created_at)}</span>
                  </div>
                  <h2 style={styles.title}>
                    {boostedIds.includes(post.id) && <span style={{ backgroundColor: '#34d399', color: '#000', fontSize: '11px', padding: '2px 8px', borderRadius: '8px', fontWeight: 'bold', marginRight: '6px', verticalAlign: 'middle' }}>📢 Sponsored</span>}
                    <a href={`/post/${post.id}`} style={{...styles.titleLink, textDecoration: 'none'}}>{post.title}</a>
                  </h2>
                  {post.image && <img src={post.image} alt={post.title} style={styles.cardImage} />}
                  <p style={styles.body}>{linkify(post.body.substring(0, 200))}...</p>
                  <div style={styles.cardFooter}>
                    <span style={styles.reward}>💰 {post.pi_reward_pool} Pi</span>
                    <button
                      style={{ ...styles.voteBtn, backgroundColor: hasVoted ? '#3ea6ff' : '#f3e5f5', color: hasVoted ? 'white' : '#3ea6ff' }}
                      onClick={(e) => { e.stopPropagation(); handleVote(post.id); }}
                      disabled={voting[post.id]}
                    >
                      👍 {post.total_votes}
                    </button>
                    <button
                      style={styles.commentToggleBtn}
                      onClick={(e) => { e.stopPropagation(); setOpenComment(prev => ({ ...prev, [post.id]: !prev[post.id] })); }}
                    >💬 {post.comments.length}</button>
                    <button
                      style={styles.tipToggleBtn}
                      onClick={(e) => { e.stopPropagation(); if (!user) { alert('Please login!'); return; } setOpenTip(prev => ({ ...prev, [post.id]: !prev[post.id] })); }}
                    >💰 Tip</button>
                    <ShareIcons title={post.title} url={`${window.location.origin}/post/${post.id}`} />
                    <a href={`/post/${post.id}`} style={styles.readMore}>Read More →</a>
                  </div>
                  {openComment[post.id] && (
                    <div style={styles.inlineBox}>
                      <textarea
                        style={styles.inlineTextarea}
                        placeholder="Write a comment..."
                        value={commentText[post.id] || ''}
                        onChange={e => setCommentText(prev => ({ ...prev, [post.id]: e.target.value }))}
                        rows={2}
                      />
                      <button
                        style={styles.inlineBtn}
                        onClick={() => handleComment(post.id)}
                        disabled={submittingComment[post.id]}
                      >{submittingComment[post.id] ? 'Posting...' : 'Post Comment'}</button>
                      {post.comments.slice(-2).map(c => (
                        <div key={c.id} style={styles.miniComment}>
                          <strong>{c.author.username}:</strong> {c.body}
                        </div>
                      ))}
                    </div>
                  )}
                  {openTip[post.id] && (
                    <div style={styles.inlineBox}>
                      <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#555' }}>Tip <strong>{post.author.username}</strong> with Pi:</p>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        {[0.1, 0.5, 1, 5].map(amt => (
                          <button
                            key={amt}
                            style={{ ...styles.inlineBtn, backgroundColor: tipAmount[post.id] === amt ? '#3ea6ff' : '#f3e5f5', color: tipAmount[post.id] === amt ? 'white' : '#3ea6ff' }}
                            onClick={() => setTipAmount(prev => ({ ...prev, [post.id]: amt }))}
                          >{amt} Pi</button>
                        ))}
                      </div>
                      <button
                        style={{ ...styles.inlineBtn, marginTop: '8px', backgroundColor: '#34d399', color: '#000' }}
                        onClick={() => window.location.href = `/post/${post.id}`}
                      >{t('send_tip_via_post')}</button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}

      <div style={styles.footer}>
        <p>© 2026 XpertCreation | CEO: Syed Khalid Hussain Shah</p>
        <p><a href="https://xpertcreation.com" style={styles.footerLink}>🌐 xpertcreation.com</a></p>
      </div>
    </div>
  );
}

const styles = {
  // Landing
  landing: {
    minHeight: '100dvh',
    paddingTop: '0',
    marginTop: '-1px',
    background: getLandingGradient(),
    position: 'relative',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  bgOrb1: { display: 'none', position: 'absolute' },
  bgOrb2: { display: 'none', position: 'absolute' },
  bgOrb3: { display: 'none', position: 'absolute' },
  landingInner: {
    width: '100%',
    padding: '0 8px',
    marginTop: '0',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  logoArea: { marginBottom: '16px' },
  logoEmoji: { fontSize: '72px', marginBottom: '10px', filter: 'drop-shadow(0 4px 12px rgba(29,78,216,0.6))' },
  logoTitle: { fontSize: '56px', fontWeight: '700', color: 'white', margin: '0 0 8px', textShadow: '0 2px 20px rgba(52,211,153,0.6)', fontFamily: "'Pacifico', cursive" },
  logoTagline: { color: 'rgba(255,255,255,0.7)', fontSize: '16px', marginBottom: '20px' },
  statsRow: { display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'nowrap', alignItems: 'center', marginBottom: '12px' },
  statPill: { backgroundColor: 'rgba(255,255,255,0.1)', color: 'white', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', border: '1px solid rgba(255,255,255,0.2)' },
  statPillLink: { backgroundColor: 'rgba(255,255,255,0.15)', color: 'white', padding: '6px 14px', borderRadius: '20px', fontSize: '14px', border: '1px solid rgba(255,255,255,0.3)', textDecoration: 'none', cursor: 'pointer' },
  navSections: { display: 'flex', flexDirection: 'column', gap: '22px', marginTop: '18px' },
  navSection: { display: 'flex', flexDirection: 'column', gap: '10px' },
  navSectionHead: { display: 'flex', alignItems: 'center', gap: '8px', padding: '0 2px' },
  navSectionIcon: { fontSize: '15px', lineHeight: 1 },
  navSectionTitle: { fontSize: '11px', fontWeight: 800, letterSpacing: '0.09em', textTransform: 'uppercase', color: '#94a3b8', whiteSpace: 'nowrap' },
  navSectionLine: { flex: 1, height: '1px', background: 'linear-gradient(90deg, rgba(148,163,184,0.35), rgba(148,163,184,0))' },
  btnGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginTop: '0px', marginBottom: '0px', width: '100%', padding: '0 6px', boxSizing: 'border-box' },
  loginBox: { marginBottom: '20px' },
  loginBoxInner: { backgroundColor: 'rgba(52,211,153,0.15)', border: '1px solid rgba(52,211,153,0.4)', borderRadius: '15px', padding: '15px 20px', display: 'inline-block' },
  loginText: { color: '#34d399', margin: 0, fontSize: '15px' },
  welcomeBox: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '15px', padding: '12px 20px', marginBottom: '20px', border: '1px solid rgba(255,255,255,0.2)' },
  welcomeAvatar: { width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', border: '2px solid #34d399' },
  welcomeText: { color: 'white', fontSize: '15px' },
  landingFooter: { color: 'rgba(255,255,255,0.4)', fontSize: '14px', marginTop: 'auto', paddingTop: '80px', paddingBottom: '16px' },
  footerLink: { color: 'rgba(255,255,255,0.6)', textDecoration: 'none' },
  // Posts view
  container: { maxWidth: '800px', margin: '0 auto', padding: '20px' },
  backBtn: { backgroundColor: '#3ea6ff', color: 'white', border: 'none', padding: '8px 20px', borderRadius: '20px', cursor: 'pointer', marginBottom: '20px', fontWeight: 'bold' },
  statsBar: { display: 'flex', justifyContent: 'space-around', backgroundColor: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', marginBottom: '20px' },
  statItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' },
  statNum: { fontSize: '22px', fontWeight: 'bold', color: '#4a00e0' },
  statLabel: { fontSize: '11px', color: '#999' },
  searchBar: { display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' },
  searchInput: { flex: 1, padding: '10px 15px', borderRadius: '25px', border: '2px solid #e0d0ff', fontSize: '14px', minWidth: '200px', outline: 'none' },
  sortSelect: { padding: '10px 15px', borderRadius: '25px', border: '2px solid #e0d0ff', fontSize: '14px', backgroundColor: 'white', cursor: 'pointer' },
  feedTitle: { color: '#333', marginBottom: '15px' },
  feed: { display: 'flex', flexDirection: 'column', gap: '20px' },
  card: { backgroundColor: 'white', borderRadius: '10px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', marginBottom: '10px', alignItems: 'center' },
  author: { color: '#4a00e0', fontWeight: 'bold', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '6px' },
  avatarSmall: { width: '24px', height: '24px', borderRadius: '50%', objectFit: 'cover' },
  date: { color: '#999', fontSize: '13px' },
  title: { margin: '10px 0' },
  titleLink: {
 color: '#333', textDecoration: 'none' },
  cardImage: { width: '100%', maxHeight: '300px', objectFit: 'cover', borderRadius: '10px', marginBottom: '10px' },
  body: { color: '#666', lineHeight: '1.6' },
  cardFooter: { display: 'flex', gap: '10px', marginTop: '15px', alignItems: 'center', flexWrap: 'wrap' },
  reward: { color: '#f9a825', fontWeight: 'bold', fontSize: '13px' },
  voteBtn: { border: '2px solid #333', textDecoration: 'none' },
  comments: { color: '#666', fontSize: '13px' },
  shareRow: { display: 'flex', gap: '5px' },
  shareBtn: (color) => ({ backgroundColor: color, color: 'white', padding: '3px 8px', borderRadius: '10px', textDecoration: 'none', fontSize: '11px', fontWeight: 'bold' }),
  readMore: { marginLeft: 'auto', color: '#4a00e0', textDecoration: 'none', fontWeight: 'bold', fontSize: '13px' },
  commentToggleBtn: { border: '2px solid #3ea6ff', padding: '4px 10px', borderRadius: '15px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', backgroundColor: '#f3e5f5', color: '#3ea6ff' },
  tipToggleBtn: { border: '2px solid #34d399', padding: '4px 10px', borderRadius: '15px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', backgroundColor: '#fffde7', color: '#0d9488' },
  inlineBox: { padding: '10px', backgroundColor: '#f9f9f9', borderTop: '1px solid #eee', borderRadius: '0 0 10px 10px' },
  inlineTextarea: { width: '100%', padding: '8px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '13px', resize: 'none', boxSizing: 'border-box' },
  inlineBtn: { padding: '6px 14px', borderRadius: '15px', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px', backgroundColor: '#3ea6ff', color: 'white' },
  miniComment: { marginTop: '6px', fontSize: '12px', color: '#444', borderTop: '1px solid #eee', paddingTop: '4px' },
  empty: { textAlign: 'center', padding: '50px', backgroundColor: 'white', borderRadius: '10px' },
  footer: { textAlign: 'center', padding: '30px 0', color: '#999', fontSize: '13px', marginTop: '20px', borderTop: '1px solid #eee' },
  loading: { textAlign: 'center', padding: '50px', fontSize: '20px' },
};

export default Home;
